import { el, setChildren, mount, unmount } from 'redom';

import application from 'application';

import { stringToSVG } from 'utilities';

import { getOperatorsByField } from './operator-selector/utilities';
import { getDefaultInputValue } from './utilities';

import DeleteActionButton from 'generics/components/delete-action-button';
import SaveButton from 'generics/components/save-button';
import SecondLevelCTA from 'generics/components/second-level-cta';

import OperatorSelector from './operator-selector';
import FilterBuilder from './filter-builder';

import conditionTypes from './condition-type-selector/condition-types';

import keys from 'constants/keys';

import './index.scss';

import closeButtonIconString from 'assets/images/close-button-icon.svg?raw';

const closeButtonIcon = stringToSVG(closeButtonIconString);

class FilterPanel {

  constructor(options) {

    this.conditions = [];

    this.customerFilter = options.customerFilter;
    this.onClose = options.onClose;
    this.onSave = options.onSave;
    this.onReady = options.onReady;
    this.onDelete = options.onDelete;
    this.FieldSelector = options.FieldSelector;
    this.scrollableParentId = options.scrollableParentId;
    this.saveFilter = options.saveFilter;
    this.disableDelete = options.disableDelete || false;

    this.fieldSelector = options.fieldSelector || null;
    this.operatorSelector = null;
    this.filterBuilder = null;
    this.header = null;
    this.nameInput = null;
    this.headerRow = null;
    this.filterBuilderRow = null;
    this.addNewConditionButton = null;
    this.newConditionButtonRow = null;
    this.actionsRow = null;
    this.mainContent = null;
    this.reusedContent = null;

    this.initializeReusedComponents();

    this.initializeHeaderRow();
    this.initializeFilterBuilderRow();
    this.initializeActionsRow();

    this.mainContent = el('#customers-filter-panel-visible-content', [this.headerRow, this.filterBuilderRow, this.actionsRow]);
    this.reusedContent = el('#customers-filter-panel-reused-components', [this.fieldSelector, this.operatorSelector]);

    this.el = el('#customers-filter-panel-main', [this.mainContent, this.reusedContent]);

  }

  initializeFieldSelector() {

    if (this.fieldSelector === null) {
      this.fieldSelector = new this.FieldSelector({
        scrollableParentId: this.scrollableParentId
      });
    } else {
      this.fieldSelector.setScrollableParentId(this.scrollableParentId);
    }

  }

  initializeOperatorSelector() {

    this.operatorSelector = new OperatorSelector({
      scrollableParentId: this.scrollableParentId
    });

  }

  initializeReusedComponents() {

    this.initializeFieldSelector();
    this.initializeOperatorSelector();

  }

  initializeHeaderRow() {

    this.header = el('h3#customer-filter-name', {
      class: 'dialog-header-text ellipsis'
    }, this.customerFilter.name, {
      onclick: this.handleHeaderClick.bind(this)
    });
    this.nameInput = el('input#customer-filter-name-input', {
      value: this.customerFilter.name,
      onkeyup: this.handleCustomerFilterNameInputKeyUpEvent.bind(this),
      onkeydown: this.handleCustomerFilterNameInputKeyDownEvent.bind(this),
      onblur: this.hideNameInput.bind(this)
    });
    const closeButton = el('button#customers-filter-panel-close-button', {
      type: 'button',
      onclick: this.handleCloseButtonClick.bind(this),
      class: 'application-button round-button'
    }, closeButtonIcon.cloneNode(true));

    this.headerRow = el('#filter-panel-header', [
      this.header,
      closeButton
    ]);

  }

  initializeFilterBuilder() {

    const options = {
      fieldSelector: this.fieldSelector,
      operatorSelector: this.operatorSelector,
      conditions: this.conditions,
      onChange: this.handleFilterBuilderChange.bind(this)
    };

    this.filterBuilder = new FilterBuilder(options);

  }

  initializeFilterBuilderRow() {

    this.filterBuilderRow = el('#customers-filter-panel-filter-builder-row', [el('ht-spinner', { size: 'small' })]);

  }

  initializeNewConditionButtonRow() {

    this.addNewConditionButton = new SecondLevelCTA({
      id: 'add-new-customer-filter-condition-button',
      label: 'Add condition',
      onClick: this.handleAddNewConditionButtonClick.bind(this),
      icon: SecondLevelCTA.icons.ADD
    });

    this.newConditionButtonRow = el('#customers-filter-panel-new-condition-button-row', [this.addNewConditionButton]);

  }

  initializeActionsRow() {

    this.initializeNewConditionButtonRow();

    const deleteButton = new DeleteActionButton({
      id: 'delete-filter-button',
      onClick: this.handleDeleteButtonClick.bind(this)
    });

    const saveSegmentButton = new SaveButton({
      id: 'save-customer-filter-segment',
      disabled: this.customerFilter.system,
      label: 'Save Segment',
      onClick: this.handleSaveSegmentButtonClick.bind(this)
    });

    const buttonsRow = [el('.placeholder')];

    if (this.customerFilter.system !== true && this.disableDelete === false) {
      buttonsRow.push(deleteButton);
    }

    buttonsRow.push(saveSegmentButton);

    const mainButtonsRow = el('#customers-filter-panel-actions-buttons-row', buttonsRow);

    this.actionsRow = el('#customers-filter-panel-actions-row', [this.newConditionButtonRow, mainButtonsRow]);

  }

  async updateOrCreateCustomerFilter(value) {

    this.customerFilter.value = value;

    this.customerFilter = await this.saveFilter(this.customerFilter);

  }

  async tryCreateCustomerFilter() {

    this.setWorking();

    this.conditions = this.customerFilter.value;

    this.setIdle();

  }

  async initialize() {

    this.countries = await application.staticData.loadCountries();

    await this.tryCreateCustomerFilter();
    await this.fieldSelector.initialize({ countries: this.countries });

    this.initializeFilterBuilder();

    setChildren(this.filterBuilderRow, this.filterBuilder);

  }

  handleCloseButtonClick() {

    this.onClose();

  }

  handleAddNewConditionButtonClick() {

    this.addNewCondition(this.fieldSelector.getFields()[0]);

  }

  async handleSaveSegmentButtonClick() {

    await this.updateOrCreateCustomerFilter(this.conditions);

    this.onSave(this.customerFilter);

  }

  handleFilterBuilderChange(conditions) {

    this.conditions = conditions;

  }

  handleCustomerFilterNameInputKeyUpEvent(event) {

    const customerFilterNameInput = event.target;

    if (customerFilterNameInput.previousValue === customerFilterNameInput.value) {
      return;
    }

    let value = customerFilterNameInput.previousValue = customerFilterNameInput.value;

    this.updateFilterName(value);

  }

  handleCustomerFilterNameInputKeyDownEvent(event) {

    const key = event.key;

    if (key === keys.ENTER) {
      this.hideNameInput();
    } else if (key === keys.ESCAPE) {
      this.hideNameInput();
    }

  }

  showNameInput() {

    mount(this.headerRow, this.nameInput, this.header);
    unmount(this.headerRow, this.header);

    this.nameInput.focus();

  }

  hideNameInput() {

    mount(this.headerRow, this.header, this.nameInput);
    unmount(this.headerRow, this.nameInput);

  }

  handleHeaderClick() {

    this.showNameInput();

  }

  updateFilterName(name) {

    this.customerFilter.name = name;
    this.header.textContent = name;

  }

  showFieldSelector() {

    const self = this;

    this.fieldSelector.reconfigure({
      field: null,
      countries: this.countries,
      scrollAnchorElement: this.addNewConditionButton.el,
      scrollableParentId: this.scrollableParentId,
      onSelect: this.handleFieldSelect.bind(this),
      onClose() {
        self.fieldSelector.hide();
      },
      placeholder: 'Search field',
      offset: {
        top: 10
      }
    });

    this.fieldSelector.show();

  }

  addNewCondition(field) {

    const fieldOperators = getOperatorsByField(field);
    const operator = fieldOperators[0];
    this.filterBuilder.addCondition({
      type: conditionTypes.AND,
      criteria: {
        field,
        operator,
        value: getDefaultInputValue(operator)
      }
    });

    this.fieldSelector.hide();

  }

  handleFieldSelect(field) {

    this.addNewCondition(field);

  }

  handleDeleteButtonClick() {

    return this.onDelete(this.customerFilter);

  }

  setWorking() {

    setChildren(this.el, [el('ht-spinner', { size: 'small' })]);

  }

  setIdle() {

    setChildren(this.el, [this.mainContent, this.reusedContent]);

  }

}

export default FilterPanel;
